'use client';

import type { ComponentProps, ReactNode } from 'react';

import { tv } from '../../lib/tv';
import {
  HoverCard as HoverCardRoot,
  HoverCardContent as HoverCardContentPrimitive,
  HoverCardTrigger,
} from '../ui/hover-card';
import { Avatar, type AvatarStatus } from './avatar';
import { hoverCardStyles } from './overlay-styles';
import { ReelThumbnail } from './reel-thumbnail';

export type HoverCardType = 'user' | 'reel';

export type HoverCardProps = ComponentProps<typeof HoverCardRoot>;

/**
 * Rich preview on hover or focus of a person or reel link (Figma: Components
 * ↳ Popover ↳ Hover Card). Opens after 500ms and closes 300ms after the
 * pointer leaves, so people can move into it. Never the only way to reach its
 * content - the link still navigates. Radix Hover Card: the card is not
 * focusable and screen readers skip it, so keep the same information on the
 * linked page. Touch devices don't hover; nothing opens there.
 */
export function HoverCard({
  openDelay = 500,
  closeDelay = 300,
  ...props
}: HoverCardProps) {
  return <HoverCardRoot openDelay={openDelay} closeDelay={closeDelay} {...props} />;
}

export type HoverCardContentProps = ComponentProps<typeof HoverCardContentPrimitive> & {
  /** Figma "Type": User 300 wide (column) · Reel 320 wide (row). */
  type?: HoverCardType;
};

/** The floating card, 8px from the link. Put a `HoverCardUser` / `HoverCardReel` inside. */
export function HoverCardContent({ type, className, ...props }: HoverCardContentProps) {
  return (
    <HoverCardContentPrimitive
      data-type={type}
      collisionPadding={8}
      className={hoverCardStyles({ type }).root({ className })}
      {...props}
    />
  );
}

/** The card drawn in place (docs, Figma frame): same classes, no Radix positioning. */
export function HoverCardPanel({
  type,
  className,
  ...props
}: ComponentProps<'div'> & { type?: HoverCardType }) {
  return (
    <div
      data-slot="hover-card-content"
      data-type={type}
      className={hoverCardStyles({ type }).root({ className })}
      {...props}
    />
  );
}

export type HoverCardStat = { value: ReactNode; unit: ReactNode };

const preview = tv({
  slots: {
    head: 'flex items-center gap-3',
    names: 'flex min-w-0 flex-1 flex-col',
    name: 'text-label-md text-text-primary truncate',
    role: 'text-caption text-text-tertiary truncate',
    bio: 'text-body-sm text-text-secondary',
    stats: 'flex flex-wrap gap-x-4',
    stat: 'flex items-baseline gap-1',
    value: 'text-label-sm text-text-primary',
    unit: 'text-caption text-text-tertiary',
    details: 'flex min-w-0 flex-1 flex-col gap-1.5',
    title: 'text-label-md text-text-primary truncate',
    meta: 'text-caption text-text-tertiary',
    reelStats: 'flex flex-wrap gap-x-3 pt-1',
  },
});

function Stats({ stats, className }: { stats: HoverCardStat[]; className: string }) {
  const styles = preview();
  return (
    <ul data-slot="hover-card-stats" className={className}>
      {stats.map((s, i) => (
        <li key={i} data-slot="hover-card-stat" className={styles.stat()}>
          <span data-slot="hover-card-stat-value" className={styles.value()}>
            {s.value}
          </span>
          <span data-slot="hover-card-stat-unit" className={styles.unit()}>
            {s.unit}
          </span>
        </li>
      ))}
    </ul>
  );
}

export type HoverCardUserProps = {
  name: string;
  /** "Creative lead · Admin". */
  role?: ReactNode;
  /** Avatar photo; falls back to initials from `name`. */
  avatarSrc?: string;
  /** Avatar status dot (Figma: success dot = online). */
  status?: AvatarStatus;
  bio?: ReactNode;
  /** "48 reels", "126K views"… value in Label/Small, unit in Caption. */
  stats?: HoverCardStat[];
  /** Full-width Small Outline Button ("View profile"). */
  action?: ReactNode;
};

/** Figma Hover Card, Type=User: Avatar 40 + name / role · bio · stats · action. */
export function HoverCardUser({
  name,
  role,
  avatarSrc,
  status,
  bio,
  stats,
  action,
}: HoverCardUserProps) {
  const styles = preview();
  return (
    <>
      <div data-slot="hover-card-head" className={styles.head()}>
        <Avatar size={40} name={name} src={avatarSrc} status={status} alt="" />
        <div data-slot="hover-card-names" className={styles.names()}>
          <p data-slot="hover-card-name" className={styles.name()}>
            {name}
          </p>
          {role && (
            <p data-slot="hover-card-role" className={styles.role()}>
              {role}
            </p>
          )}
        </div>
      </div>
      {bio && (
        <p data-slot="hover-card-bio" className={styles.bio()}>
          {bio}
        </p>
      )}
      {stats?.length ? <Stats stats={stats} className={styles.stats()} /> : null}
      {action}
    </>
  );
}

export type HoverCardReelProps = {
  title: ReactNode;
  /** Thumbnail image / video (fills the 72 × 128 frame); the gradient shows without it. */
  thumbnail?: ReactNode;
  /** Duration chip, e.g. "0:30". */
  duration?: ReactNode;
  /** A Small status Badge ("Ready"). */
  status?: ReactNode;
  /** Caption lines: "9:16 · 12 variations", "Edited 2h ago by Maya K.". */
  meta?: ReactNode[];
  stats?: HoverCardStat[];
};

/** Figma Hover Card, Type=Reel: 9:16 thumbnail · title, status, meta lines, stats. */
export function HoverCardReel({
  title,
  thumbnail,
  duration,
  status,
  meta = [],
  stats,
}: HoverCardReelProps) {
  const styles = preview();
  return (
    <>
      <ReelThumbnail duration={duration}>{thumbnail}</ReelThumbnail>
      <div data-slot="hover-card-details" className={styles.details()}>
        <p data-slot="hover-card-title" className={styles.title()}>
          {title}
        </p>
        {status && <div className="flex">{status}</div>}
        {meta.map((line, i) => (
          <p key={i} data-slot="hover-card-meta" className={styles.meta()}>
            {line}
          </p>
        ))}
        {stats?.length ? <Stats stats={stats} className={styles.reelStats()} /> : null}
      </div>
    </>
  );
}

export { HoverCardTrigger };
